<template>
	<div>
		<div class="gh-layout">
			<aside class="gh-intro">
				<h2 class="gh-intro__title">Auto-Campaign Hooks</h2>
				<p class="gh-intro__desc">
					Webhooks that trigger mc2 auto campaigns. Campaigns always enroll a <em>municipality</em> —
					for client hooks mc2 resolves the municipality from the mapped mc2 client.
					<strong>Test</strong> is a dry run: it shows the resolved municipality and the campaigns that
					would fire. <strong>Run</strong> creates real campaign runs.
				</p>
			</aside>

			<section class="gh-accounts">
				<div v-if="loadingHooks" class="text-muted">Loading…</div>

				<div v-for="h in hooks" :key="h.hook" class="ada-card mb-3">
					<div class="gh-section">
						<div class="gh-section__title">
							<i class="fas fa-bolt me-1"></i> {{ h.label }}
							<code class="hook-path ms-2">{{ h.hook }}</code>
						</div>
						<div class="gh-help">{{ h.description }}</div>

						<div class="gh-field mt-2">
							<label>Active campaigns on this hook</label>
							<div v-if="h.active_campaigns && h.active_campaigns.length">
								<span v-for="c in h.active_campaigns" :key="c.id" class="perm-chip">
									{{ c.name }}
								</span>
							</div>
							<div v-else class="text-muted">No active campaigns — a run will not enroll anything.</div>
						</div>

						<!-- Target picker: juniper client or municipality, per hook type -->
						<div v-if="hookState[h.hook]" class="gh-field mt-2">
							<template v-if="h.input === 'client'">
								<label>Client</label>
								<select v-model="hookState[h.hook].clientId" class="form-select">
									<option value="">Select a client…</option>
									<option v-for="c in hookClients" :key="c.id" :value="c.id">
										{{ c.name }} — {{ c.url }}{{ c.status === 'live' ? '' : ' (' + (c.status || 'no status') + ')' }}
									</option>
								</select>
								<div class="gh-help">mc2 resolves the municipality from the mc2 client mapped to this juniper client.</div>
							</template>

							<template v-else>
								<label>Municipality</label>
								<input
									type="text"
									class="form-control"
									v-model="hookState[h.hook].municipalitySearch"
									placeholder="Search municipality… (name or name, state)"
									@input="onHookMunicipalitySearch(h.hook)"
								/>
								<div v-if="hookState[h.hook].searching" class="gh-help">Searching…</div>
								<ul v-if="hookState[h.hook].municipalities.length" class="hook-muni-results">
									<li
										v-for="m in hookState[h.hook].municipalities"
										:key="m.id"
										@click="selectHookMunicipality(h.hook, m)"
									>
										{{ m.full_name }}, {{ m.state }}
										<span class="text-muted">— {{ m.county }} county, pop. {{ m.population || '?' }}</span>
									</li>
								</ul>
								<div v-if="hookState[h.hook].selectedMunicipality" class="gh-help">
									Selected: <strong>{{ hookState[h.hook].selectedMunicipality.full_name }}, {{ hookState[h.hook].selectedMunicipality.state }}</strong>
									(id {{ hookState[h.hook].selectedMunicipality.id }})
								</div>
							</template>
						</div>

						<div v-if="hookState[h.hook]" class="d-flex gap-2 mt-3">
							<button
								class="gh-save-btn gh-save-btn--sm"
								:disabled="hookState[h.hook].testing || hookState[h.hook].running"
								@click="runHook(h, true)"
							>
								<i class="fas me-1" :class="hookState[h.hook].testing ? 'fa-spinner fa-spin' : 'fa-vial'"></i>
								{{ hookState[h.hook].testing ? 'Testing…' : 'Test hook' }}
							</button>
							<button
								class="gh-save-btn gh-save-btn--sm"
								:disabled="hookState[h.hook].testing || hookState[h.hook].running"
								@click="runHook(h, false)"
							>
								<i class="fas me-1" :class="hookState[h.hook].running ? 'fa-spinner fa-spin' : 'fa-bolt'"></i>
								{{ hookState[h.hook].running ? 'Running…' : 'Run hook' }}
							</button>
						</div>

						<!-- Recent executions of this hook (from mc2 auto_campaign_hook_logs) -->
						<div v-if="hookLogsByHook(h.hook).length" class="gh-field mt-3">
							<label>Recent activity</label>
							<div class="hook-log-wrap">
								<table class="hook-log-table">
									<thead>
										<tr>
											<th>When</th>
											<th>Mode</th>
											<th>Status</th>
											<th>Municipality</th>
											<th>Campaigns</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="log in hookLogsByHook(h.hook)" :key="log.id">
											<td :title="log.created_at">{{ fromNowUtc(log.created_at) }}</td>
											<td><span class="hook-log-mode" :class="'hook-log-mode--' + log.mode">{{ log.mode }}</span></td>
											<td>
												<span :class="log.status === 'success' ? 'text-success' : 'text-danger'" :title="log.error || ''">
													{{ log.status }}
												</span>
											</td>
											<td>{{ log.municipality_name || '—' }}</td>
											<td>
												<span
													v-if="(log.runs || []).length"
													class="hook-log-campaigns"
													data-bs-toggle="tooltip"
													data-bs-placement="top"
													:title="hookLogCampaignsTooltip(log)"
												>
													{{ log.campaigns_triggered }}
												</span>
												<span v-else>{{ log.campaigns_triggered }}</span>
											</td>
											<td class="hook-log-actions">
												<button
													class="hook-log-delete"
													type="button"
													title="Delete log entry"
													:disabled="deletingHookLogId === log.id"
													@click="deleteHookLog(log)"
												>
													<i class="fas" :class="deletingHookLogId === log.id ? 'fa-spinner fa-spin' : 'fa-trash-alt'"></i>
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- Result of the last test/run -->
						<div v-if="hookState[h.hook] && hookState[h.hook].result" class="hook-result mt-3">
							<div class="hook-result__head" :class="hookState[h.hook].result.ok ? 'hook-result__head--ok' : 'hook-result__head--bad'">
								<i class="fas me-1" :class="hookState[h.hook].result.ok ? 'fa-check-circle' : 'fa-times-circle'"></i>
								{{ hookState[h.hook].result.message }}
							</div>
							<div v-if="hookState[h.hook].result.result && hookState[h.hook].result.result.municipality" class="hook-result__body">
								<div>
									<strong>Municipality:</strong>
									{{ hookState[h.hook].result.result.municipality.full_name }},
									{{ hookState[h.hook].result.result.municipality.state }}
									(id {{ hookState[h.hook].result.result.municipality.id }},
									resolved via {{ hookState[h.hook].result.result.search_method }})
								</div>
								<div v-if="hookState[h.hook].result.result.mode === 'test'">
									<strong>Would trigger:</strong>
									<span v-if="hookState[h.hook].result.result.campaigns.length">
										{{ hookState[h.hook].result.result.campaigns.map((c) => c.name).join(', ') }}
									</span>
									<span v-else>no active campaigns</span>
								</div>
								<div v-if="hookState[h.hook].result.result.mode === 'run'">
									<strong>Runs created:</strong>
									<span v-if="hookState[h.hook].result.result.runs.length">
										<span v-for="r in hookState[h.hook].result.result.runs" :key="r.campaign_id" class="me-2">
											{{ r.campaign_name }} → {{ r.run_code || 'failed' }}
										</span>
									</span>
									<span v-else>none — no active campaigns on this hook</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>

		<ConfirmModal
			ref="deleteLogModal"
			title="Delete log entry?"
			message="This permanently removes this activity entry."
			confirm-text="Delete"
			confirm-icon="fa-trash-alt"
			icon-class="fa-trash-alt"
			danger
			:busy="deletingHookLogId != null"
			@confirm="confirmDeleteHookLog"
			@cancel="pendingDeleteLog = null"
		/>
	</div>
</template>

<script>
import { Tooltip } from 'bootstrap'
import restApi from '@/api'
import juniperApi from '@/api/juniperApi'
import { showGlobalToast } from '@/utils/toast'
import { fromNowUtc } from '@/utils/time'
import ConfirmModal from '@/components/common/ConfirmModal.vue'

export default {
	name: 'HooksSettings',
	components: { ConfirmModal },
	data() {
		return {
			hooks: [],
			hookClients: [],
			hookLogs: [],
			loadingHooks: false,
			hookState: {},
			deletingHookLogId: null,
			pendingDeleteLog: null,
		}
	},
	created() {
		this.loadHooks()
		this.loadHookClients()
		this.loadHookLogs()
	},
	methods: {
		async loadHooks() {
			this.loadingHooks = true
			try {
				const { data } = await restApi.get('/auto-campaign-hooks')
				this.hooks = data?.hooks || []
				const state = {}
				this.hooks.forEach((h) => {
					state[h.hook] = {
						clientId: '',
						municipalitySearch: '',
						municipalities: [],
						selectedMunicipality: null,
						searching: false,
						testing: false,
						running: false,
						result: null,
					}
				})
				this.hookState = state
			} catch (e) {
				console.error('Failed to load hooks', e)
				showGlobalToast('Failed to load hooks', 'danger')
			} finally {
				this.loadingHooks = false
			}
		},
		// The live-client hook targets a juniper client, so the picker list
		// still comes from juniper-api.
		async loadHookClients() {
			try {
				const { data } = await juniperApi.get('/clients', { params: { all: true } })
				this.hookClients = Array.isArray(data) ? data : []
			} catch (e) {
				console.error('Failed to load clients for hooks tab', e)
			}
		},
		async loadHookLogs() {
			try {
				const { data } = await restApi.get('/auto-campaign-hooks/logs', { params: { limit: 50 } })
				this.hookLogs = data?.logs || []
				this.$nextTick(() => this.initHookTooltips())
			} catch (e) {
				console.error('Failed to load hook logs', e)
			}
		},
		hookLogsByHook(hook) {
			return this.hookLogs.filter((log) => log.hook === hook).slice(0, 5)
		},
		deleteHookLog(log) {
			this.pendingDeleteLog = log
			this.$refs.deleteLogModal.open()
		},
		async confirmDeleteHookLog() {
			const log = this.pendingDeleteLog
			if (!log) return

			this.deletingHookLogId = log.id
			try {
				await restApi.delete(`/auto-campaign-hooks/logs/${log.id}`)
				this.hookLogs = this.hookLogs.filter((l) => l.id !== log.id)
				this.$nextTick(() => this.initHookTooltips())
				showGlobalToast('Log entry deleted', 'success')
				this.$refs.deleteLogModal.close()
				this.pendingDeleteLog = null
			} catch (e) {
				showGlobalToast(e?.response?.data?.message || 'Failed to delete log entry', 'danger')
			} finally {
				this.deletingHookLogId = null
			}
		},
		hookLogCampaignsTooltip(log) {
			return (log.runs || [])
				.map((r) => (r.run_code ? `${r.campaign_name} → ${r.run_code}` : r.campaign_name))
				.join(', ')
		},
		// (Re)attach Bootstrap tooltips on the activity tables. Rows re-render
		// on every logs refresh, so stale instances are disposed first.
		initHookTooltips() {
			this.$el.querySelectorAll('.hook-log-table [data-bs-toggle="tooltip"]').forEach((el) => {
				const existing = Tooltip.getInstance(el)
				if (existing) existing.dispose()
				new Tooltip(el)
			})
		},
		onHookMunicipalitySearch(hook) {
			const st = this.hookState[hook]
			st.selectedMunicipality = null
			if (this._hookSearchTimers && this._hookSearchTimers[hook]) {
				clearTimeout(this._hookSearchTimers[hook])
			}
			const search = st.municipalitySearch.trim()
			if (search.length < 2) {
				st.municipalities = []
				return
			}
			this._hookSearchTimers = this._hookSearchTimers || {}
			this._hookSearchTimers[hook] = setTimeout(async () => {
				st.searching = true
				try {
					const { data } = await restApi.get('/auto-campaign-hooks/municipality-search', { params: { search } })
					st.municipalities = data?.municipalities || []
				} catch (e) {
					st.municipalities = []
				} finally {
					st.searching = false
				}
			}, 350)
		},
		selectHookMunicipality(hook, municipality) {
			const st = this.hookState[hook]
			st.selectedMunicipality = municipality
			st.municipalitySearch = `${municipality.full_name}, ${municipality.state}`
			st.municipalities = []
		},
		async runHook(hookDef, test) {
			const st = this.hookState[hookDef.hook]
			const payload = { hook: hookDef.hook, test }

			if (hookDef.input === 'client') {
				if (!st.clientId) {
					showGlobalToast('Select a client first', 'danger')
					return
				}
				const client = this.hookClients.find((c) => c.id === st.clientId)
				payload.juniper_id = st.clientId
				payload.website = client?.url
			} else {
				if (!st.selectedMunicipality) {
					showGlobalToast('Select a municipality first', 'danger')
					return
				}
				payload.municipality_id = st.selectedMunicipality.id
			}

			if (!test) {
				const confirm = await this.$swal.fire({
					icon: 'warning',
					title: 'Run hook?',
					text: 'This creates real campaign runs for every active campaign attached to this hook.',
					showCancelButton: true,
					confirmButtonText: 'Run hook',
				})
				if (!confirm.isConfirmed) return
			}

			if (test) {
				st.testing = true
			} else {
				st.running = true
			}
			st.result = null
			try {
				const { data } = await restApi.post('/auto-campaign-hooks/manual-run', payload)
				st.result = { ok: true, message: data?.message, result: data }
				showGlobalToast(test ? 'Test completed' : 'Hook executed', 'success')
			} catch (e) {
				const body = e?.response?.data || {}
				st.result = { ok: false, message: body.message || 'Hook call failed', result: body }
				showGlobalToast(body.message || 'Hook call failed', 'danger')
			} finally {
				st.testing = false
				st.running = false
				this.loadHookLogs()
			}
		},
		fromNowUtc,
	},
}
</script>

<style scoped lang="scss">
.hook-path {
	font-size: 12px;
	color: #6c757d;
	background: #f1f3f5;
	padding: 2px 6px;
	border-radius: 4px;
	text-transform: none;
	letter-spacing: 0;
}

.hook-muni-results {
	list-style: none;
	margin: 4px 0 0;
	padding: 0;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	max-height: 220px;
	overflow: auto;

	li {
		padding: 6px 10px;
		cursor: pointer;
		font-size: 13px;

		&:hover {
			background: #f1f3f5;
		}

		+ li {
			border-top: 1px solid #f1f3f5;
		}
	}
}

.hook-log-wrap {
	overflow-x: auto;
}

.hook-log-table {
	width: 100%;
	font-size: 13px;
	border-collapse: collapse;

	th {
		text-align: left;
		font-weight: 600;
		color: #6c757d;
		border-bottom: 1px solid #dee2e6;
		padding: 4px 10px 4px 0;
	}

	td {
		padding: 4px 10px 4px 0;
		border-bottom: 1px solid #f1f3f5;
		white-space: nowrap;
	}
}

.hook-log-campaigns {
	text-decoration: underline dotted;
	text-underline-offset: 2px;
	cursor: help;
}

.hook-log-actions {
	width: 1%;
	text-align: right;
}

.hook-log-delete {
	border: 0;
	background: none;
	padding: 2px 4px;
	color: #adb5bd;
	cursor: pointer;

	&:hover:not(:disabled) {
		color: #dc3545;
	}

	&:disabled {
		cursor: default;
	}
}

.hook-log-mode {
	display: inline-block;
	border-radius: 10px;
	padding: 1px 8px;
	font-size: 11px;
	font-weight: 600;

	&--webhook {
		background: #e7f1ff;
		color: #0b5ed7;
	}

	&--manual {
		background: #fff3cd;
		color: #997404;
	}

	&--test {
		background: #e2e3e5;
		color: #41464b;
	}
}

.hook-result {
	border: 1px solid #dee2e6;
	border-radius: 6px;
	overflow: hidden;

	&__head {
		padding: 6px 10px;
		font-size: 13px;
		font-weight: 600;

		&--ok {
			background: #d1e7dd;
			color: #0f5132;
		}

		&--bad {
			background: #f8d7da;
			color: #842029;
		}
	}

	&__body {
		padding: 8px 10px;
		font-size: 13px;

		> div + div {
			margin-top: 4px;
		}
	}
}
</style>
